(() => {
  if (customElements.get('oilogic-awards-slider')) return;

/*
 * <oilogic-awards-slider> — Embla Carousel wrapper for the Awards section.
 * Embla (self-hosted vendor asset, v8.6.0) drives the slider: infinite loop,
 * mouse/touch drag, snap alignment and arrow navigation, mirroring the
 * behavior of <oilogic-social-feed> exactly:
 *   - mobile: active card snaps 31px from the left edge (Figma) so the next
 *     card's tilted corner peeks in; tablet+ snaps to center.
 *   - autoplay (opt-in) pauses on hover/focus and honors reduced motion.
 *   - when every card fits, data-no-overflow is stamped and Embla stays off
 *     (CSS centers the set and hides the arrows).
 *
 * Progressive enhancement: until this element stamps data-ready, the track is
 * a native CSS scroll-snap overflow scroller, fully usable without JS. Embla
 * only boots on top of that markup (viewport wrapper + flex track).
 *
 * Expected markup (hooks are data attributes, styling is the consumer's):
 *   <oilogic-awards-slider>
 *     <div data-slider-viewport>
 *       <ul data-feed-track> … </ul>
 *     </div>
 *     <button data-feed-prev>…</button>
 *     <button data-feed-next>…</button>
 *   </oilogic-awards-slider>
 */
class OilogicAwardsSlider extends HTMLElement {
  #embla = null;
  #Embla = null;
  #ctrl = new AbortController();
  #resizeTimer = 0;

  connectedCallback() {
    this.viewport = this.querySelector('[data-slider-viewport]');
    this.track = this.querySelector('[data-feed-track]');
    this.prevBtn = this.querySelector('[data-feed-prev]');
    this.nextBtn = this.querySelector('[data-feed-next]');
    if (!this.viewport || !this.track || typeof window.oilogicEmbla !== 'function') return;

    const { signal } = this.#ctrl;
    this.prevBtn?.addEventListener('click', () => this.#embla?.scrollPrev(), { signal });
    this.nextBtn?.addEventListener('click', () => this.#embla?.scrollNext(), { signal });
    // Embla re-measures on resize itself; this only re-checks the
    // fits/overflows boundary, which decides whether Embla runs at all.
    window.addEventListener('resize', () => this.#onResize(), { passive: true, signal });

    // Embla core is lazy-loaded once per page via the shared singleton loader.
    // Card widths are token-driven (not image-dependent), so layout is stable
    // by the next frame — safe to measure and boot once the library resolves.
    window.oilogicEmbla(this.dataset.emblaUrl).then((Embla) => {
      if (this.#ctrl.signal.aborted) return;
      this.#Embla = Embla;
      requestAnimationFrame(() => this.#init());
    }).catch(() => {});
  }

  disconnectedCallback() {
    clearTimeout(this.#resizeTimer);
    this.#teardown();
    this.#ctrl.abort();
    this.#ctrl = new AbortController();
  }

  #onResize() {
    clearTimeout(this.#resizeTimer);
    this.#resizeTimer = setTimeout(() => this.#init(), 150);
  }

  #init() {
    if (!this.#Embla) return;
    this.#teardown();
    const overflowing = this.track.scrollWidth - this.viewport.clientWidth > 1;
    this.toggleAttribute('data-no-overflow', !overflowing);
    if (!overflowing) return;

    this.dataset.ready = '';

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const plugins = [];
    if (this.hasAttribute('data-autoplay') && !reduce && typeof EmblaCarouselAutoplay !== 'undefined') {
      const secs = parseFloat(this.getAttribute('data-autoplay-speed')) || 4;
      plugins.push(
        EmblaCarouselAutoplay({
          delay: secs * 1000,
          stopOnInteraction: false,
          stopOnMouseEnter: true,
          stopOnFocusIn: true,
        })
      );
    }

    this.#embla = this.#Embla(
      this.viewport,
      {
        loop: true,
        // Mobile: snap the card 31px from the viewport's left edge (matches
        // the old scroll-padding-inline). Tablet+: center, like before.
        align: () => 31,
        breakpoints: {
          '(min-width: 768px)': { align: 'center' },
        },
        duration: reduce ? 0 : 25,
      },
      plugins
    );
  }

  #teardown() {
    this.#embla?.destroy();
    this.#embla = null;
    delete this.dataset.ready;
  }
}

customElements.define('oilogic-awards-slider', OilogicAwardsSlider);
})();
